import React, { useRef, useEffect, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { MAPBOX_TOKEN } from '../config';

// Disable Mapbox workers to avoid CORS and CSP issues on deployment
// The map will work without workers (slightly slower tile loading, but functional)
if (typeof window !== 'undefined') {
  // @ts-ignore - Disable workers completely
  mapboxgl.workerClass = null;
}

mapboxgl.accessToken = MAPBOX_TOKEN;

const MapboxMap = ({ 
  longitude = -73.5673, 
  latitude = 45.5017, 
  zoom = 13,
  markerTitle = '',
  markerDescription = '',
  height = '400px',
  className = ''
}) => {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const marker = useRef(null);
  const [mapError, setMapError] = useState(null);
  const [mapLoaded, setMapLoaded] = useState(false);

  useEffect(() => {
    if (map.current) return; // Initialize map only once

    // Check if token is available
    if (!mapboxgl.accessToken || mapboxgl.accessToken === 'undefined') {
      console.error('Mapbox token not found!');
      setMapError('Map configuration error');
      return;
    }

    // Add delay to ensure container is ready (especially important for dialogs/modals)
    const initTimer = setTimeout(() => {
      try {
        // Verify container exists and has dimensions
        if (!mapContainer.current) {
          console.error('❌ Map container not found');
          setMapError('Container not ready');
          return;
        }

        const containerWidth = mapContainer.current.offsetWidth;
        const containerHeight = mapContainer.current.offsetHeight;
        
        if (containerWidth === 0 || containerHeight === 0) {
          console.error('❌ Map container has no dimensions:', { containerWidth, containerHeight });
          setMapError('Container sizing error - please close and reopen the map');
          return;
        }

        console.log('✅ Initializing Mapbox map...');

        map.current = new mapboxgl.Map({
          container: mapContainer.current,
          style: 'mapbox://styles/mapbox/streets-v12',
          center: [longitude, latitude],
          zoom: zoom,
        });

        // Set loaded state when map loads
        map.current.on('load', () => {
          setMapLoaded(true);
          console.log('✅ Map loaded successfully');
        });

        // Add error handling
        map.current.on('error', (e) => {
          console.error('❌ Mapbox error:', e);
          if (!e.error?.message?.includes('postMessage')) {
            setMapError('Map loading error');
          }
        });

        // Add navigation controls
        map.current.addControl(new mapboxgl.NavigationControl(), 'top-right');

    // Add marker
    const el = document.createElement('div');
    el.className = 'mapbox-marker';
    el.style.backgroundColor = '#ef4444';
    el.style.width = '32px';
    el.style.height = '32px';
    el.style.borderRadius = '50% 50% 50% 0';
    el.style.transform = 'rotate(-45deg)';
    el.style.border = '3px solid white';
    el.style.boxShadow = '0 2px 4px rgba(0,0,0,0.3)';
    el.style.cursor = 'pointer';

    marker.current = new mapboxgl.Marker(el)
      .setLngLat([longitude, latitude])
      .addTo(map.current);

    // Add popup if title is provided
    if (markerTitle) {
      const popup = new mapboxgl.Popup({ offset: 25, closeButton: false })
        .setHTML(`
          <div style="padding: 8px;">
            <h3 style="font-weight: 600; margin: 0 0 4px 0; font-size: 14px;">${markerTitle}</h3>
            ${markerDescription ? `<p style="margin: 0; font-size: 12px; color: #666;">${markerDescription}</p>` : ''}
          </div>
        `);

      marker.current.setPopup(popup);
      
      // Show popup on hover
      el.addEventListener('mouseenter', () => {
        marker.current.togglePopup();
      });
    }

      } catch (error) {
        console.error('Mapbox initialization error:', error);
        setMapError(error.message);
      }
    }, 200); // 200ms delay to ensure container is ready

    // Cleanup on unmount
    return () => {
      clearTimeout(initTimer);
      try {
        if (marker.current) marker.current.remove();
        if (map.current) map.current.remove();
      } catch (error) {
        console.error('Mapbox cleanup error:', error);
      }
    };
  }, []);

  // Handle map resize when container becomes visible (for modal support)
  useEffect(() => {
    if (!map.current || !mapContainer.current) return;

    // Suppress ResizeObserver loop errors (common with Mapbox in modals)
    const resizeErrorHandler = (e) => {
      if (e.message === 'ResizeObserver loop completed with undelivered notifications.' ||
          e.message.includes('ResizeObserver')) {
        e.stopImmediatePropagation();
        return true;
      }
    };
    window.addEventListener('error', resizeErrorHandler);

    const observer = new ResizeObserver(() => {
      if (map.current && mapContainer.current?.offsetWidth > 0) {
        setTimeout(() => {
          try {
            map.current.resize();
          } catch (err) {
            // Silently handle resize errors
          }
        }, 100);
      }
    });

    observer.observe(mapContainer.current);

    return () => {
      observer.disconnect();
      window.removeEventListener('error', resizeErrorHandler);
    };
  }, []);

  // Update marker position when coordinates change
  useEffect(() => {
    if (marker.current && map.current) {
      marker.current.setLngLat([longitude, latitude]);
      map.current.flyTo({
        center: [longitude, latitude],
        zoom: zoom,
        duration: 1000
      });
    }
  }, [longitude, latitude, zoom]);

  return (
    <div className="relative">
      {!mapLoaded && !mapError && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100 rounded-xl z-10">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-rose-500 mb-3"></div>
            <p className="text-gray-600">Loading map...</p>
          </div>
        </div>
      )}
      <div
        ref={mapContainer}
        className={`mapbox-container rounded-xl overflow-hidden ${className}`}
        style={{ width: '100%', height }}
      />
      {mapError && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100 rounded-xl">
          <div className="text-center p-4">
            <p className="text-gray-600">📍 Map temporarily unavailable</p>
            <p className="text-sm text-gray-500 mt-2">View location on Google Maps</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default MapboxMap;
